<template>
  <div class="h-full flex flex-col bg-white">
    <div class="flex-1 overflow-y-auto">
      <div class="p-3 sm:p-6">
        <div class="text-2xl sm:text-3xl font-bold mb-6">
          Add a payment method
          <span v-if="isPaymentOptional">(optional)</span>
        </div>
        <div class="text-sm bg-amber-100 text-yellow-800 border border-yellow-600 p-4 rounded mb-4">
          No charges will be made unless you accept this proposal, and billing will start according to the services
          outlined.
        </div>
        <div
          v-if="intentLoading"
          class="w-full my-3 flex min-h-20 items-center justify-center"
        >
          <client-loading-indicator class="text-primary-500 fill-gray-100 w-5 h-5" />
        </div>
        <div v-else>
          <div v-if="intentError">
            <p class="text-red-500">
              There was an error loading this page. Please try refreshing the screen or contact support at
              support@financial-cents.com.
            </p>
          </div>
          <div v-else>
            <div
              class="py-2 max-w-[595px]"
              v-if="intent && intent.provider === 'rainforest'"
            >
              <portal-payment-method-selector
                :allowed="allowedMethods"
                v-model="method"
                :card-fee="intent.processing_fees"
              />
            </div>
            <div class="-mx-4">
              <portal-rainforest-payment-method-form
                v-if="intent && intent.provider === 'rainforest'"
                :intent="intent"
                hide-card-brand-logos
                hide-button
                :allowed-methods="method"
                @valid="$emit('form-valid', true)"
                @approve="paymentMethodApproved"
                @loaded="intentLoading = false"
                @attempted="$emit('submit-loading', true)"
                @invalid="$emit('form-valid', false)"
              />

              <portal-billing-proposal-stripe-payment-method-form
                v-if="intent && intent.provider === 'stripe'"
                :intent="intent"
                :proposal="proposalData"
                @payment-method-change="stripeMethod = $event"
                @loaded="intentLoading = false"
                @approve="paymentMethodApproved"
                ref="stripePaymentMethodForm"
                @valid="$emit('form-valid', true)"
                @invalid="$emit('form-valid', false)"
              />

              <div
                v-if="intent.processing_fees && (intent.provider === 'stripe' ? showStripeFees : method === 'CARD')"
                class="px-5 text-gray-500 text-xs"
              >
                A processing fee of {{ intent.processing_fees }}% applies
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Proposal from "../../repository/Proposal"
import ClientLoadingIndicator from "../../../ClientPortal/client_auth/components/client-loading-indicator.vue"
import PortalPaymentMethodSelector from "../../../ClientPortal/components/invoice_payment/PortalPaymentMethodSelector.vue"
import PortalRainforestPaymentMethodForm from "../../../ClientPortal/components/invoice_payment/PortalRainforestPaymentMethodForm.vue"
import PortalBillingProposalStripePaymentMethodForm from "./PortalBillingProposalStripePaymentMethodForm.vue"

export default {
  name: "PortalBillingProposalPayment",

  emits: ["next-step", "disable-continue-button", "form-valid", "submit-loading", "set-provider"],

  props: {
    isPaymentOptional: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    PortalBillingProposalStripePaymentMethodForm,
    PortalRainforestPaymentMethodForm,
    PortalPaymentMethodSelector,
    ClientLoadingIndicator,
  },

  data() {
    return {
      intentLoading: true,
      proposalData: window.FinancialCents.proposal,
      intent: null,
      //hasPaymentStep: window.FinancialCents.proposal.require_payment_details,
      //rainforestFormValid: false,
      intentError: false,
      method: "CARD",
      stripeMethod: null,
    }
  },

  computed: {
    allowedMethods() {
      const isCard = (item) => {
        return item.toLowerCase() === "card" || item.toLowerCase() === "cc"
      }

      return this.proposalData.allowed_payment_types
        .map((item) => (isCard(item) ? "CARD" : "ACH"))
        .sort()
        .reverse()
    },

    showStripeFees() {
      if (this.intent?.provider === "stripe") {
        return this.stripeMethod === "card"
      }

      return false
    },
  },

  watch: {
    method() {
      this.intentLoading = true
      this.$nextTick(() => {
        this.intentLoading = false
      })
    },
  },

  methods: {
    paymentMethodApproved() {
      this.$emit("submit-loading", false)
      this.$emit("next-step")
    },

    submit() {
      if (this.intent.provider === "stripe") {
        return this.$refs.stripePaymentMethodForm.confirmMethod()
      }

      if (this.intent.provider === "rainforest") {
        const component = document.querySelector("rainforest-payment")
        component.submit()
      }
    },

    async createIntent() {
      try {
        const { data } = await Proposal.paymentMethodIntent(this.proposalData.id)
        this.intent = data.data
        this.stripeMethod = data.provider === "stripe" ? "CARD" : "ACH"
        this.$emit("set-provider", this.intent?.provider)
      } catch (e) {
        console.error(e)
        this.intentError = true
      }
      this.intentLoading = false
    },
  },

  async mounted() {
    this.method = this.allowedMethods.includes("CARD") ? "CARD" : "ACH"

    this.$emit("disable-continue-button")
    await this.createIntent()
  },
}
</script>
